
import externalConfiguration from '../external-configuration';
import { appendQueryString, avoidCache, patchM3u8Url, patchHttpsFromJson, IE11_safeIncludes, IE11_safeFrameElement, arrayShuffle } from './utils';

import { loadTaggingTranscription, loadTaggingListTranscription } from './tagging-to-state';

import { geo_html5_country, geoip2_country } from './playback-restrictions';

function vdmReadMedia( media, settings) {
  const mediaSettings = {
    file: settings.basePlayerOptions.progressive ? patchHttpsFromJson(media.progressive) : patchM3u8Url(media.m3u8),
    image: externalConfiguration.defaultImageDown,
    duration: media.duration,
    title: settings.displayTitle ? media.title : undefined,
    progressive: patchHttpsFromJson(media.progressive),
  };
  if( media.thumbnail) {
    mediaSettings.image = avoidCache( media.thumbnail.url);
  }
  mediaSettings.tracks = [{
    file: avoidCache( media.storyboard),
    kind: "thumbnails",
  }];
  if( media.chapter) {
    mediaSettings.tracks.push({
        file: avoidCache( media.chapter.url),
        kind: "chapters",
    });
  }
  if( media.subtitles) media.subtitles.forEach( media_has_subtitle => {
    mediaSettings.tracks.push( {
      file: avoidCache( (media_has_subtitle.subtitle && media_has_subtitle.subtitle.url) || media_has_subtitle),
      label: media_has_subtitle.languageTag || media_has_subtitle.name,
      kind: "captions",
    });
  });
  return mediaSettings;
}

function vdmReadPlaylist( playlist, settings) {
  const type = playlist && playlist.medias && playlist.medias[0] && (playlist.medias[0].type || playlist.medias[0].media && playlist.medias[0].media.type);
  settings.visualizerOptions.srcType = type === 'mp4' ? 'vod' : 'live';   // PATCH para permitir que chat configure el formato inicial de la fecha junto a los comentarios // en el futuro hacer esta y otras opciones similiares, configurables por el cliente desde la interfaz
  settings.playlist = playlist.medias.sort( (media1,media2) => media1.order - media2.order ).map( media => vdmReadMedia( media.media || media, settings ));
  if( 'duration' in playlist && playlist.duration) {
    settings.playlist.duration = playlist.duration;
  } else {
    settings.playlist.duration = playlist.medias.map( media => media.duration).reduce( (duration1, duration2) => duration1 + duration2, 0);
  }
}

function vdmReadContent( media, playlist, contentMedias, multichannelIconFile, settings) {
  switch( settings.contentType) {
    case 'SingleMedia':
      vdmReadPlaylist({ medias: [media]}, settings);
      break;
    case 'Playlist':
      vdmReadPlaylist( playlist, settings);
      break;
    case 'MainWithBackups':
      settings.basePlayerOptions.mainWithBackups = {
        current: 0,
        medias: contentMedias.map( content => vdmReadMedia( content.media, settings)),
      };
      vdmReadPlaylist({ medias: [contentMedias[0]]}, settings);
      break;
    case 'BalancedFallbacks':
      settings.basePlayerOptions.balancedFallbacks = {
        current: 0,
        medias: contentMedias.map( content => vdmReadMedia( content.media, settings)),
      };
      vdmReadPlaylist({ medias: [contentMedias[0]]}, settings);
      break;
    case 'MultiChannel':
      settings.basePlayerOptions.multiChannel = {
        current: 0,
        mainIconUrl: multichannelIconFile && multichannelIconFile.url,
        medias: contentMedias.map( content => vdmReadMedia( content.media, settings)),
        iconUrls: contentMedias.map( content => content.iconFile && content.iconFile.url),
      };
      vdmReadPlaylist({ medias: [contentMedias[0]]}, settings);
      break;
    default:
      throw 'Error: contentMediasType no reconocido.';
  }
}

function adaptStart( now, start, repetition) {
  let adaptedStart = start;
  switch( repetition) {
    case 'daily':
      adaptedStart = new Date( now.getFullYear(), now.getMonth(), now.getDate(), start.getHours(), start.getMinutes(), start.getSeconds());
      while( now < adaptedStart) {    // REVIEW usar if en lugar de while, por que en teoria nunca deberia ser mas de uno ??
        adaptedStart.setDate( adaptedStart.getDate() -1);
      }
      break;
    case 'weekly':
      const adaptedDay = now.getDate() - now.getDay() + start.getDay();
      adaptedStart = new Date( now.getFullYear(), now.getMonth(), adaptedDay, start.getHours(), start.getMinutes(), start.getSeconds());
      while( now < adaptedStart) {    // REVIEW usar if en lugar de while, por que en teoria nunca deberia ser mas de uno ??
        adaptedStart.setDate( adaptedStart.getDate() -7);
      }
      break;
    case 'monthly':
      adaptedStart = new Date( now.getFullYear(), now.getMonth(), start.getDate(), start.getHours(), start.getMinutes(), start.getSeconds());
      while( now < adaptedStart) {    // REVIEW usar if en lugar de while, por que en teoria nunca deberia ser mas de uno ??
        adaptedStart.setMonth( adaptedStart.getMonth() -1);
      }
      break;
    case 'yearly':
      adaptedStart = new Date( now.getFullYear(), start.getMonth(), start.getDate(), start.getHours(), start.getMinutes(), start.getSeconds());
      while( now < adaptedStart) {    // REVIEW usar if en lugar de while, por que en teoria nunca deberia ser mas de uno ??
        adaptedStart.setYear( adaptedStart.getFullYear() -1);
      }
      break;
    case 'cyclic':
    case 'once':
    default:;
  }
  return adaptedStart;
}

function adaptEnd( adaptedStart, repetition, durationMS, absoluteEnd) {
  let adaptedEnd = absoluteEnd;
  switch( repetition) {
    case 'once':
    case 'daily':
    case 'weekly':
    case 'monthly':
    case 'yearly':
      adaptedEnd = new Date( Math.min( adaptedStart.getTime() + durationMS, absoluteEnd));
      break;
    case 'cyclic':
    default:;
  }
  return adaptedEnd;
}

function vdmSetRepetition( repetition, settings) {
  const modeTranslate = { cyclic: 'continualLoop', daily: 'diary'};
  const isProgrammation = IE11_safeIncludes( [ 'once', 'cyclic', 'daily', 'weekly', 'monthly', 'yearly'], repetition);
  if( IE11_safeIncludes( [ 'cyclic', 'daily', 'weekly', 'monthly', 'yearly'], repetition)) {
    settings.customProps.repeat = true;
  }

  let startFromVideo = 0;
  let startFromSecond = 0;
  let now = new Date();
  if( isProgrammation) {
    const start = new Date( settings.playbackRestrictions.publishDate);
    if( now >= start) {
      const end = new Date( settings.playbackRestrictions.unpublishDate);
      const adaptedStart = adaptStart( now, start, repetition);
      const adaptedEnd = adaptEnd( adaptedStart, repetition, settings.playlist.duration * 1000, end);
      const mode = repetition in modeTranslate ? modeTranslate[repetition] : repetition;
      startFromSecond = (now - adaptedStart) / 1000;
      settings.playbackRestrictions.unpublishDate = adaptedEnd;
      settings.basePlayerOptions.programmationInfo = { mode, finalDate: end};
      settings.basePlayerOptions.programmationInfo.effectiveTimeStampStart = adaptedStart;

      if( repetition === 'cyclic') {  // REVIEW se puede quitar si se ajusta correctamente el adaptedStart, pero puede ser mas lento
        startFromSecond %= settings.playlist.duration;
        settings.basePlayerOptions.programmationInfo.effectiveTimeStampStart = new Date(now.getTime() - startFromSecond * 1000);
      }

      settings.playlist.some( (e,i) => {
        if( startFromSecond <= e.duration){
          return true;
        }
        startFromSecond -= e.duration;
        startFromVideo += 1;
        return false;
      });
      settings.basePlayerOptions.disableManualSeek = true;
      settings.customProps.autostart = true;   // Cuando alguna de las dos siguientes tiene un valor distinto de 0, el autostart es implicito; se pone explicito para el caso particular 0,0
      settings.basePlayerOptions.startFromVideo = startFromVideo;
      settings.basePlayerOptions.startFromSecond = startFromSecond;
    }
  }
}

function createVastTemplate( mp4Route) {
  mp4Route = mp4Route.replace(/&/g,'&amp;');
  return `<VAST version="2.0">
   <Ad id="static">
     <InLine>
       <AdSystem>Static VAST Template</AdSystem>
       <AdTitle>Static VAST Tag</AdTitle>
       <Creatives>
         <Creative>
           <Linear>
             <MediaFiles>
               <MediaFile type="video/mp4">${mp4Route}</MediaFile>
             </MediaFiles>
            </Linear>
         </Creative>
       </Creatives>
     </InLine>
   </Ad>
 </VAST>
 `;
}

// sample: https://github.com/InteractiveAdvertisingBureau/VAST_Samples/blob/master/VAST%203.0%20Samples/Inline_Non-Linear_Tag-test.xml

function createVastOverlayTemplate( imageUrl) {
  imageUrl = imageUrl.replace(/&/g,'&amp;');
  return `<VAST version="2.0">
   <Ad id="static">
     <InLine>
       <AdSystem>Static VAST Template</AdSystem>
       <AdTitle>Static VAST Tag</AdTitle>
       <Creatives>
         <Creative>
           <NonLinearAds>
             <NonLinear>
               <StaticResource creativeType="image/png">${imageUrl}</StaticResource>
             </NonLinear>
            </NonLinearAds>
         </Creative>
       </Creatives>
     </InLine>
   </Ad>
 </VAST>
 `;
}

function selectRollWithRestrictions( rolls, location) {   // REVIEW testear a fondo
  let selectedRoll = false;
  let defaultRoll = false;
  rolls.forEach( roll => {
    if( !selectedRoll && roll.startTimeRestriction && roll.endTimeRestriction) {
      const nowDate = new Date();
      const now = nowDate.getUTCHours() * 60 + nowDate.getMinutes();
      const startText = roll.startTimeRestriction.split(':');
      const start = parseInt(startText[0]) * 60 + parseInt(startText[1]);
      const endText = roll.endTimeRestriction.split(':');
      const end = parseInt(endText[0]) * 60 + parseInt(endText[1]);
      if( now >= start && now <= end) {
        selectedRoll = roll;
      }
    }
    if( !selectedRoll && roll.countryRestriction) {
      if( roll.countryRestriction === location.country || roll.countryRestriction === location.province) {
        selectedRoll = roll;
      }
    }
    if( roll.default) {
      defaultRoll = roll;
    }
  });
  if( !selectedRoll) {
    selectedRoll = defaultRoll;
  }
  return selectedRoll;
}

function calculateRegion( advert) {
  const rollTypes = [ 'preRolls', 'postRolls'];
  let foundRestriction = false;
  return new Promise( (resolve,reject) => {
    if( advert) {
      rollTypes.forEach( rollType => {
        if( rollType in advert) {
          advert[rollType].forEach( roll => {
            if( roll.countryRestriction) {
              foundRestriction = true;
            }
          });
        }
      });
    }
    if( foundRestriction) {
      geo_html5_country().then( location => {  // latitude, longitude, country, aal_1, aal_2, city, street, results
        const country = location.country;
        const province = `${country}-${location.aal_2}`;
        resolve({ country, province});
      }).catch( err => {
        geoip2_country().then( location => {   // country, subdivision_0, subdivision_1, response
          const country = location.country;
          const province = location.subdivision_1;
          resolve({ country, province});
        });
      });
    } else {
      resolve({ country: '*', province: '*'});
    }
  });
}

function vdmReadAdServer( adserver, settings) {
  if( adserver) {
    const advertising = {
      client: 'vast',
    };
    const adschedules = settings.playlist.map( () => ({}));
    let startFromVideo = settings.basePlayerOptions.startFromVideo;
    if( !startFromVideo || startFromVideo < 0 || startFromVideo >= adschedules.length) startFromVideo = 0;
    adschedules[ startFromVideo].videoHeader = {    // TODO garantizar en base-player que videoHeader se reproduce solo una vez
      offset: 'pre',
      tag: adserver,
      // skipoffset: preRoll.skipTime,
    };
    settings.customProps.advertising = advertising;
    settings.playlist.forEach( (media,i) => {
      if( adschedules[i] !== {}) media.adschedule = adschedules[i];
    });
  }
}

function vdmReadAdvert( advert, settings) {
  return calculateRegion( advert).then( location => {
    const findMaxBitrateMp4Url = ( bitrates) => {
      const defaultBitrate = { bitrate: -1, filename: '//'};
      const maxBitrate = bitrates.reduce( (prev,current) => prev.bitrate > current.bitrate ? prev : current, defaultBitrate);
      return maxBitrate.url;
    }
    const advertising = {
      client: 'vast',
    };
    const adschedules = settings.playlist.map( () => ({}));
    let time = 0;
    let video = 0;
    let setAnyAd = false;
    if( advert && 'preRolls' in advert) {
      const preRoll = selectRollWithRestrictions( advert.preRolls, location);
      if( preRoll) {
        setAnyAd = true;
        const mp4Url = findMaxBitrateMp4Url( preRoll.media.bitrates);
        adschedules[0].preRoll = {
          offset: 'pre',
          tag: `data:text/xml,${ createVastTemplate( appendQueryString(mp4Url,{advertId:advert.id,preRollId:preRoll.id}))}`,
          skipoffset: preRoll.skipTime,
        };
        const startFromVideo = settings.basePlayerOptions.startFromVideo;
        if( startFromVideo && startFromVideo > 0 && startFromVideo < adschedules.length) {
          adschedules[ startFromVideo].videoHeader = {    // TODO garantizar en base-player que videoHeader se reproduce solo una vez
            offset: 'pre',
            tag: `data:text/xml,${ createVastTemplate( appendQueryString(mp4Url,{advertId:advert.id,preRollId:preRoll.id}))}`,
            skipoffset: preRoll.skipTime,
          };
        }
      }
    }
    if( advert && 'midRolls' in advert) {
      const midRolls = advert.shuffle ? arrayShuffle( advert.midRolls) : advert.midRolls;
      midRolls.forEach( (midRoll,i) => {
        time += advert.secondsBetweenAds;
        while( video < settings.playlist.length && time > settings.playlist[ video].duration) {
          time -= settings.playlist[ video++].duration;
        } if( video < settings.playlist.length) {
          setAnyAd = true;
          const mp4Url = findMaxBitrateMp4Url( midRoll.media.bitrates);
          adschedules[ video][`midRoll${i++}`] = {
            offset: time,
            tag: `data:text/xml,${ createVastTemplate( appendQueryString(mp4Url,{advertId:advert.id,midRollId:midRoll.id}))}`,
            skipoffset: midRoll.skipTime,
          };
        }
      });
    }
    if( advert && 'overlays' in advert) {
      const overlays = advert.overlays;
      overlays.forEach( (overlay,i) => {
        time += advert.secondsBetweenOverlays;
        while( video < settings.playlist.length && time > settings.playlist[ video].duration) {
          time -= settings.playlist[ video++].duration;
        } if( video < settings.playlist.length) {
          setAnyAd = true;
          adschedules[ video][`overlay${i++}`] = {
            offset: time,
            tag: `data:text/xml,${ createVastOverlayTemplate( overlay.src)}`,
          };
        }
      });
    }
    if( advert && 'postRolls' in advert) {
      const postRoll = selectRollWithRestrictions( advert.postRolls, location);
      if( postRoll) {
        setAnyAd = true;
        const mp4Url = findMaxBitrateMp4Url( postRoll.media.bitrates);
        adschedules[adschedules.length-1].postRoll = {
          offset: 'post',
          tag: `data:text/xml,${ createVastTemplate( appendQueryString(mp4Url,{advertId:advert.id,postRollId:postRoll.id}))}`,
        };
      }
    };
    if( setAnyAd) {
      settings.customProps.advertising = advertising;
      settings.playlist.forEach( (media,i) => {
        if( adschedules[i] !== {}) media.adschedule = adschedules[i];
      });
    }
    return settings;
  });
}

function vdmReadSecureDomains( secureDomains, settings) {
  const active = secureDomains && secureDomains.length > 0;
  settings.playbackRestrictions.domainBlockActive = active;
  settings.playbackRestrictions.domainsBlocked = active ? secureDomains : [];
}

function vdmReadGeoblocking( geoblocking, settings) {
  const active = geoblocking && geoblocking.length > 0;
  let geoblockingAsObject = {};
  geoblocking.forEach( country => {
    // geoblockingAsObject[ country.alpha2Code] = country.name;
    geoblockingAsObject[ country] = country;
  });
  settings.playbackRestrictions.countryBlockActive = active;
  settings.playbackRestrictions.countriesBlocked = active ? geoblockingAsObject : {};
}

export function vdmReadSharing( networks, settings) {
  const ReplaceNeworkTable = { mail: 'email'};
  if( networks && networks.length > 0) {
    const frameElement = IE11_safeFrameElement();
    const code = frameElement ? frameElement.outerHTML : '<iframe src="' + document.URL + '"></iframe>';
    const sites = networks.map( network => (network in ReplaceNeworkTable ? ReplaceNeworkTable[network] : network));
    settings.customProps.sharing = {
      code: code,
      link: /src=["']([^"']*)["']/.exec(code)[1],
      sites: sites,
    };
  } else {
    settings.customProps.sharing = undefined;   // Soporte para modificacion onthefly
  }
}

export function vdmReadDesign( design, settings) {
  settings.customProps.skin = externalConfiguration.defaultDesign;
  if( design.colorBar && 'r' in design.colorBar && 'g' in design.colorBar && 'b' in design.colorBar && 'a' in design.colorBar) {
    settings.customProps.skin.background = `rgba(${design.colorBar.r}, ${design.colorBar.g}, ${design.colorBar.b}, ${design.colorBar.a})`;
  }
  if( design.logoUrl && design.logoUrl !== '') {
    const validPositions = [ 'top-left', 'top-right', 'bottom-left', 'bottom-right'];
    const position = 'logoPosition' in design && validPositions.indexOf(design.logoPosition) > -1 ? design.logoPosition : 'top-left';
    settings.customProps.logo = {
      file: avoidCache( design.logoUrl),
      position,
    };
    if( 'logoLink' in design && design.logoLink !== '') {
      settings.customProps.logo.link = design.logoLink;
    };
  };
}

function vdmReadTagging( taggingStyle, settings, taggingList = []) {
  settings.visualizerType = 'Tagging';
  settings.customProps.width = window.innerWidth * externalConfiguration.taggingSettings.playerSize;
  settings.visualizerOptions.onResize = state => {
    state.customProps.width = window.innerWidth * externalConfiguration.taggingSettings.playerSize;
    state.customProps.height = window.innerHeight;
    return state;
  };
  if( taggingList && taggingList.length)
    return loadTaggingListTranscription( { taggingStyle }, taggingList, settings);
  return loadTaggingTranscription( settings, undefined, { taggingStyle });
}

function vdmReadChat( chatOptions, settings) {
  if( !chatOptions.disable) {
    settings.visualizerType = 'Chat';
    settings.customProps.width = window.innerWidth * externalConfiguration.chatSettings.playerSize;
    let showingChat = true;
    settings.chatSettings = { iframeId: chatOptions.id, hasRegister: chatOptions.lock, showingChat};    // TODO cambiar iframeId por chatId
    settings.visualizerOptions.onResize = state => {
      const chatPlayerSize = showingChat ? externalConfiguration.chatSettings.playerSize : 1.0;
      state.customProps.width = window.innerWidth * chatPlayerSize;
      state.customProps.height = window.innerHeight;
      return state;
    }
    const onShowHide = state => {
      showingChat = !showingChat;
      const chatPlayerSize = showingChat ? externalConfiguration.chatSettings.playerSize : 1.0;
      state.customProps.width = window.innerWidth * chatPlayerSize;
      state.chatSettings.showingChat = showingChat;
      return state;
    }
    settings.basePlayerOptions.extraButtons = [{ imgSrc: '/vdmplayer/resources/Chat_ShowHide.svg', text: 'Mostrar / Ocultar Chat', handler: onShowHide, id: 'M/OChat'}];
    settings.chatSettings.onShowHide = onShowHide;
    return settings;
  }

  return settings;
}

function vdmToState( iframeId, _ = undefined, queryParams = {}) {
  // let iframeUrl = `/testIframe.json`    // TESTING
  // let iframeUrl = `${externalConfiguration.apiDirectDomain}${externalConfiguration.apiPublicPath}iframe/${iframeId}`    // DEVELOP
  let iframeUrl = `${externalConfiguration.apiCDNDomain}${externalConfiguration.apiPublicPath}iframe/${iframeId}`
  if( iframeId === 'default.iframe') {
    iframeUrl = '/vdmplayer/default.iframe.json';
  }
  if( iframeId === 'tagging-default.iframe') {
    iframeUrl = '/vdmplayer/tagging-default.iframe.json';
  }
  return fetch( iframeUrl).then( response => response.json()).then( response => {
    const baseSettings = JSON.stringify( response);
    const contentType = response.contentMediasType || (response.playlist ? 'Playlist' : 'SingleMedia');

    const settings = {
      id: response.id,
      legacy: true,             // TESTING, finish building base-player, then remove
      visualizerType: 'Vdm',
      contentType,
      customProps: {
        hlshtml: true,
        primary: "flash",
        abouttext: externalConfiguration.defaultPlayerAboutText,
        aboutlink: externalConfiguration.defaultPlayerAboutLink,
        width: window.innerWidth,
        height: window.innerHeight,
        autostart: !!response.autoplay,
        cast: response.cast ? {} : undefined,
      },
      visualizerOptions: {
        onResize: state => {
          state.customProps.width = window.innerWidth;
          state.customProps.height = window.innerHeight;
          return state;
        },
      },
      basePlayerOptions: {
        download: response.download,
        progressive: response.progressive,
        platformId: response.platformId,
      },
      playbackRestrictions: {
        state: response.active,
        publishDate: response.publishDate,
        unpublishDate: response.unpublishDate,
        // publishTimeStamp: response.publishDate,  // En la plataforma nueva no es necesario timestamp por que los Date estan siempre en GMT
        // unpublishTimeStamp: response.unpublishDate,
        displayImageWhenBlock: externalConfiguration.defaultBlockImage,   // TODO remplazar por imagenes parametrizadas dependiendo de la razon de bloqueo
      },
      displayTitle: response.displayTitle,
    };
    if( response.autoRefresh) {
      settings.testSettingsForRefresh = () => fetch( iframeUrl).then( response => response.json()).then( response => baseSettings !== JSON.stringify(response));
    }
    if( response.relatedMediaUrl) {
      settings.customProps.related = {
        file: response.relatedMediaUrl,
      };
    }
    vdmReadSecureDomains( response.secureDomains, settings);
    vdmReadGeoblocking( response.geoblocking, settings);
    vdmReadDesign( response.design, settings);
    vdmReadSharing( response.socialSharing, settings);
    vdmReadContent( response.media, response.playlist, response.contentMedias, response.contentMediasMultiChannelIconFile, settings);
    if( response.errorMedia) settings.errorMedia = vdmReadMedia( response.errorMedia, settings);
    vdmSetRepetition( response.repetition, settings);                     // Importante que cargue despues de vdmReadPlaylist
    return vdmReadAdvert( response.advert, settings).then( settings => {  // Importante que cargue despues de vdmSetRepetition
      vdmReadAdServer( response.adserver, settings);
      if( response.activeTagging) {
        const taggingList = queryParams.transcriptionUrl ? [{ name: 'ES', url: queryParams.transcriptionUrl }] : response.taggingList;    // TODO reorganizar la carga para no requerir el uso de query-params desde aqui
        return vdmReadTagging( response.taggingStyle, settings, taggingList);
      }
      if( response.chat) {
        return vdmReadChat( response.chat, settings);
      }
      return settings;
    });
  });
}

export default vdmToState;



// WEBPACK FOOTER //
// ./src/helpers/vdm-to-state.js